/**
 * SVG export — a line-for-line port of pbBuildFootballSVG / pbBuildConeSVG /
 * pbBuildStarSVG / pbBuildPlaySVGContent / pbBuildPlayElementsSVG /
 * pbBuildStandaloneSVG. The golden test in svg.test.ts pins the output; any
 * change here must be deliberate because coaches' printed cards depend on it.
 */
import type { PlayData } from '../types';
import {
  pbMarkerLabel,
  PB_COLORS,
  PB_DRILL_H,
  PB_DRILL_W,
  PB_LOS_PCT,
  PB_NAME_PCT,
  PB_SCR_HASH_OFF,
  PB_SCR_ICON_REF,
  PB_SCR_LOS_PCT,
  PB_SCR_YD_DOWN,
  PB_SCR_YD_TOTAL,
  PB_SCR_YD_UP,
  PB_Y10_PCT,
  PB_Y5_PCT,
  pbBaseRadius,
  pbGetPlayDims,
  pbIsLineman,
} from './constants';
import { buildZigzagPoints } from './geometry';
import { lineCardPlay } from './lineCard';

export function escapeHtml(s: unknown): string {
  return String(s ?? '').replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}

export function buildFootballSVG(cx: number, cy: number, rw: number): string {
  const rh = rw * 0.62;
  let s = `<g transform="translate(${cx.toFixed(1)},${cy.toFixed(1)}) rotate(-17)">\n`;
  s += `<ellipse rx="${rw.toFixed(1)}" ry="${rh.toFixed(1)}" fill="#cc7700"/>\n`;
  s += `<line x1="${(-rw * 0.25).toFixed(1)}" y1="0" x2="${(rw * 0.25).toFixed(1)}" y2="0" stroke="#000" stroke-width="0.7"/>\n`;
  [-0.12, 0, 0.12].forEach((t) => {
    const x = (t * rw * 1.6).toFixed(1);
    s += `<line x1="${x}" y1="${(-rh * 0.55).toFixed(1)}" x2="${x}" y2="${(rh * 0.55).toFixed(1)}" stroke="#000" stroke-width="0.7"/>\n`;
  });
  s += `</g>\n`;
  return s;
}

export function buildConeSVG(cx: number, cy: number, r: number): string {
  const h = r * 1.6,
    halfBase = r * 0.8,
    top = cy - h * 0.55,
    bot = cy + h * 0.45;
  const pts = `${cx.toFixed(1)},${top.toFixed(1)} ${(cx + halfBase).toFixed(1)},${bot.toFixed(1)} ${(cx - halfBase).toFixed(1)},${bot.toFixed(1)}`;
  const sw = Math.max(0.4, r * 0.12);
  return `<polygon points="${pts}" fill="#ff7a18" stroke="#a34200" stroke-width="${sw.toFixed(2)}" stroke-linejoin="round"/>\n`;
}

// ── Drill props ────────────────────────────────────────────────────────────
// Each of these is reached only by a type code that did not exist before drill
// diagrams (BAG / SLED / HOOP / QUEUE / COACH), so no play or card built
// earlier can take these branches.

/** Blocking bag / dummy: a rounded bar lying on the grass. */
export function buildBagSVG(cx: number, cy: number, r: number): string {
  const w = r * 2.3,
    h = r * 0.95;
  const sw = Math.max(0.4, r * 0.1);
  return `<rect x="${(cx - w / 2).toFixed(1)}" y="${(cy - h / 2).toFixed(1)}" width="${w.toFixed(1)}" height="${h.toFixed(1)}" rx="${(h / 2).toFixed(1)}" fill="#2f6f4f" stroke="#1b422f" stroke-width="${sw.toFixed(2)}"/>\n`;
}

/** Two-man sled: a frame with a pair of pads on the front edge. */
export function buildSledSVG(cx: number, cy: number, r: number): string {
  const w = r * 2.6,
    h = r * 1.5;
  const sw = Math.max(0.4, r * 0.1);
  const x = cx - w / 2,
    y = cy - h / 2;
  let s = `<rect x="${x.toFixed(1)}" y="${(y + h * 0.42).toFixed(1)}" width="${w.toFixed(1)}" height="${(h * 0.58).toFixed(1)}" rx="${(r * 0.2).toFixed(1)}" fill="#555f6b" stroke="#2c333b" stroke-width="${sw.toFixed(2)}"/>\n`;
  [0.27, 0.73].forEach((t) => {
    const px = x + w * t;
    s += `<rect x="${(px - r * 0.42).toFixed(1)}" y="${y.toFixed(1)}" width="${(r * 0.84).toFixed(1)}" height="${(h * 0.5).toFixed(1)}" rx="${(r * 0.2).toFixed(1)}" fill="#7b8794" stroke="#2c333b" stroke-width="${sw.toFixed(2)}"/>\n`;
  });
  return s;
}

/** Agility hoop / ring laid flat. */
export function buildHoopSVG(cx: number, cy: number, r: number): string {
  const sw = Math.max(0.8, r * 0.26);
  return `<ellipse cx="${cx.toFixed(1)}" cy="${cy.toFixed(1)}" rx="${(r * 1.05).toFixed(1)}" ry="${(r * 0.62).toFixed(1)}" fill="none" stroke="#ffd24d" stroke-width="${sw.toFixed(2)}"/>\n`;
}

/** A line of players waiting a rep. `count` comes from the marker's label. */
export function buildQueueSVG(cx: number, cy: number, r: number, count: number): string {
  const n = Math.max(2, Math.min(8, count));
  const rr = r * 0.62,
    gap = rr * 1.5;
  let s = '';
  for (let i = n - 1; i >= 0; i--) {
    const y = cy + i * gap;
    const op = (1 - i * 0.09).toFixed(2);
    s += `<circle cx="${cx.toFixed(1)}" cy="${y.toFixed(1)}" r="${rr.toFixed(1)}" fill="#6b7fd7" stroke="#3a4a97" stroke-width="${Math.max(0.4, rr * 0.16).toFixed(2)}" opacity="${op}"/>\n`;
  }
  return s;
}

/** The coach: a dark rounded square so it never reads as a player circle. */
export function buildCoachSVG(cx: number, cy: number, r: number): string {
  const a = r * 1.5;
  const fs = Math.max(3, Math.round(r * 0.95));
  let s = `<rect x="${(cx - a / 2).toFixed(1)}" y="${(cy - a / 2).toFixed(1)}" width="${a.toFixed(1)}" height="${a.toFixed(1)}" rx="${(r * 0.3).toFixed(1)}" fill="#1a1a16" stroke="#000000" stroke-width="${Math.max(0.4, r * 0.1).toFixed(2)}"/>\n`;
  s += `<text x="${cx.toFixed(1)}" y="${cy.toFixed(1)}" text-anchor="middle" dominant-baseline="central" font-size="${fs}" font-weight="bold" font-family="sans-serif" fill="#fff">C</text>\n`;
  return s;
}

export function buildStarSVG(cx: number, cy: number, r: number, fill: string): string {
  const spikes = 5,
    outer = r,
    inner = r * 0.45;
  const pts: string[] = [];
  for (let i = 0; i < spikes * 2; i++) {
    const rad = (i * Math.PI) / spikes - Math.PI / 2,
      dist = i % 2 === 0 ? outer : inner;
    pts.push(`${(cx + dist * Math.cos(rad)).toFixed(1)},${(cy + dist * Math.sin(rad)).toFixed(1)}`);
  }
  const sw = Math.max(0.4, r * 0.08);
  return `<polygon points="${pts.join(' ')}" fill="${fill}" stroke="#000" stroke-width="${sw.toFixed(2)}"/>\n`;
}

export interface SvgGeo {
  stroke: number;
  arrow: number;
  cap: number;
  zig: number;
}

/** The routes-and-players half of an exported diagram. */
export function buildPlayElementsSVG(
  play: PlayData | null,
  W: number,
  H: number,
  mode: string,
  refW?: number,
  geo?: SvgGeo,
): string {
  if (!play) return '';
  const pRProminent = pbBaseRadius(W, H, refW, mode);
  const pRFaded = pbBaseRadius(W, H, refW) * 0.8;
  const G = geo || { stroke: H * 0.018, arrow: H * 0.06, cap: H * 0.04, zig: H * 0.055 };
  let g = '';
  const strokeW = G.stroke;
  play.lines.forEach((line) => {
    if (!line.points || line.points.length < 2) return;
    let color = line.color || '#333';
    if (mode === 'line' && color === '#c9c6ef') color = '#444444';
    if (mode === 'line' && color === '#8e2ee0') color = '#6600bb';
    const pts = line.points.map((p) => `${(p.x * W).toFixed(1)},${(p.y * H).toFixed(1)}`).join(' ');
    if (line.style === 'motion') {
      const rawPts = line.points.map((p) => ({ x: p.x * W, y: p.y * H }));
      const zp = buildZigzagPoints(rawPts, G.zig);
      const zpts = zp.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
      g += `<polyline points="${zpts}" fill="none" stroke="${color}" stroke-width="${strokeW.toFixed(2)}" stroke-linecap="round" stroke-linejoin="round"/>\n`;
    } else if (line.style === 'block') {
      g += `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="${strokeW.toFixed(2)}" stroke-linecap="round" stroke-linejoin="round"/>\n`;
      const lp = line.points[line.points.length - 1]!,
        pp = line.points[line.points.length - 2]!;
      const lx = lp.x * W,
        ly = lp.y * H,
        px2 = pp.x * W,
        py2 = pp.y * H;
      const ang = Math.atan2(ly - py2, lx - px2),
        perp = ang + Math.PI / 2,
        csz = G.cap;
      g += `<line x1="${(lx + csz * Math.cos(perp)).toFixed(1)}" y1="${(ly + csz * Math.sin(perp)).toFixed(1)}" x2="${(lx - csz * Math.cos(perp)).toFixed(1)}" y2="${(ly - csz * Math.sin(perp)).toFixed(1)}" stroke="${color}" stroke-width="${(strokeW * 1.3).toFixed(2)}" stroke-linecap="square"/>\n`;
    } else {
      const dash = line.style === 'dash' ? ` stroke-dasharray="${(strokeW * 2).toFixed(1)},${(strokeW * 1.3).toFixed(1)}"` : '';
      const isArrow = !line.style || line.style === 'arrow';
      if (isArrow && line.points.length >= 2) {
        const rawPts = line.points.map((p) => ({ x: p.x * W, y: p.y * H }));
        const endPt = rawPts[rawPts.length - 1]!;
        let angle = 0;
        for (let bi = rawPts.length - 2; bi >= 0; bi--) {
          const dx = endPt.x - rawPts[bi]!.x,
            dy = endPt.y - rawPts[bi]!.y;
          if (Math.sqrt(dx * dx + dy * dy) > 6) {
            angle = Math.atan2(dy, dx);
            break;
          }
        }
        const sz = G.arrow,
          ax = endPt.x,
          ay = endPt.y;
        const shortened = rawPts.slice(0, -1).concat([{ x: ax - sz * 0.7 * Math.cos(angle), y: ay - sz * 0.7 * Math.sin(angle) }]);
        const spts = shortened.map((p) => `${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
        g += `<polyline points="${spts}" fill="none" stroke="${color}" stroke-width="${strokeW.toFixed(2)}"${dash} stroke-linecap="round" stroke-linejoin="round"/>\n`;
        const apts = `${ax.toFixed(1)},${ay.toFixed(1)} ${(ax - sz * Math.cos(angle - Math.PI / 7)).toFixed(1)},${(ay - sz * Math.sin(angle - Math.PI / 7)).toFixed(1)} ${(ax - sz * Math.cos(angle + Math.PI / 7)).toFixed(1)},${(ay - sz * Math.sin(angle + Math.PI / 7)).toFixed(1)}`;
        g += `<polygon points="${apts}" fill="${color}"/>\n`;
      } else {
        g += `<polyline points="${pts}" fill="none" stroke="${color}" stroke-width="${strokeW.toFixed(2)}"${dash} stroke-linecap="round" stroke-linejoin="round"/>\n`;
      }
    }
  });
  play.players.forEach((p) => {
    if (p.type === 'BALL') {
      g += buildFootballSVG(p.x * W, p.y * H, pRProminent * 0.95);
      return;
    }
    if (p.type === 'CONE') {
      g += buildConeSVG(p.x * W, p.y * H, pRProminent * 0.95);
      return;
    }
    if (p.type === 'BAG') {
      g += buildBagSVG(p.x * W, p.y * H, pRProminent * 0.95);
      return;
    }
    if (p.type === 'SLED') {
      g += buildSledSVG(p.x * W, p.y * H, pRProminent * 0.95);
      return;
    }
    if (p.type === 'HOOP') {
      g += buildHoopSVG(p.x * W, p.y * H, pRProminent * 0.95);
      return;
    }
    if (p.type === 'QUEUE') {
      g += buildQueueSVG(p.x * W, p.y * H, pRProminent * 0.95, parseInt(p.label || '3', 10) || 3);
      return;
    }
    if (p.type === 'COACH') {
      g += buildCoachSVG(p.x * W, p.y * H, pRProminent * 0.95);
      return;
    }
    const cfg = PB_COLORS[p.type] || PB_COLORS['C']!;
    const isl = pbIsLineman(p.type);
    const faded = mode !== 'scratch' && ((mode === 'skill' && isl) || (mode === 'line' && !isl));
    const lineCard = mode === 'line';
    const r = faded ? pRFaded : pRProminent;
    const fill = faded ? '#ccccdd' : cfg.fill;
    const tclr = faded ? '#555' : lineCard && isl ? '#fff' : cfg.text;
    const lbl = escapeHtml(pbMarkerLabel(p));
    const px = (p.x * W).toFixed(1),
      py = (p.y * H).toFixed(1);
    const op = faded ? 0.35 : 1;
    const linemanOnLineCard = lineCard && isl && !faded;
    const tfs = linemanOnLineCard ? Math.max(4, Math.round(r * 1.55)) : Math.max(3, Math.round(r * 1.1));
    const isPuller = p.type === 'PULL';
    const pullerStroke = mode === 'skill' && isPuller && !faded ? '#8e2ee0' : null;
    const strokeC = faded ? '#000' : pullerStroke || '#000';
    const strokeW2 = faded ? 0.6 : pullerStroke ? 1.8 : 0.9;
    if (p.ballCarrier) {
      g += buildStarSVG(p.x * W, p.y * H, r * 1.15, fill);
    } else {
      g += `<circle cx="${px}" cy="${py}" r="${r.toFixed(1)}" fill="${fill}" stroke="${strokeC}" stroke-width="${strokeW2}" opacity="${op}"/>\n`;
    }
    if (linemanOnLineCard) {
      g += `<text x="${px}" y="${py}" text-anchor="middle" dominant-baseline="central" font-size="${tfs}" font-weight="bold" font-family="sans-serif" fill="${tclr}" stroke="#000" stroke-width="0.4" paint-order="stroke" opacity="${op}">${lbl}</text>\n`;
    } else {
      g += `<text x="${px}" y="${py}" text-anchor="middle" dominant-baseline="central" font-size="${tfs}" font-weight="bold" font-family="sans-serif" fill="${tclr}" opacity="${op}">${lbl}</text>\n`;
    }
  });
  return g;
}

export function buildPlaySVGContent(
  play: PlayData | null,
  W: number,
  H: number,
  mode: string,
  refW?: number,
): { content: string; markerDefs: string } {
  // A line card is the blocking scheme only: backs and receivers are left off.
  if (play && mode === 'line') play = lineCardPlay(play);
  const los = H * PB_LOS_PCT,
    y5 = H * PB_Y5_PCT,
    y10 = H * PB_Y10_PCT;
  const nameY = H * (1 - PB_NAME_PCT);
  const fs = Math.round(H * 0.065);
  const arrowColors: Record<string, true> = {};
  (play?.lines || []).forEach((l) => {
    if (l.color && (!l.style || l.style === 'arrow')) arrowColors[l.color] = true;
  });
  const markerDefs = Object.keys(arrowColors)
    .map((c) => {
      const id = 'arr_' + c.replace(/[^a-zA-Z0-9]/g, '');
      return `<marker id="${id}" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><path d="M0,0 L7,3.5 L0,7 Z" fill="${c}"/></marker>`;
    })
    .join('\n');
  let g = '';
  g += `<rect width="${W}" height="${H}" fill="white"/>\n`;
  g += `<line x1="14" y1="${y10.toFixed(1)}" x2="${W - 3}" y2="${y10.toFixed(1)}" stroke="#000" stroke-opacity="0.2" stroke-width="0.6" stroke-dasharray="3,3"/>\n`;
  g += `<text x="2" y="${(y10 + fs * 0.85).toFixed(1)}" font-size="${fs}" fill="#000" fill-opacity="0.4" font-family="sans-serif" font-weight="bold">10</text>\n`;
  g += `<line x1="10" y1="${y5.toFixed(1)}" x2="${W - 3}" y2="${y5.toFixed(1)}" stroke="#000" stroke-opacity="0.2" stroke-width="0.6" stroke-dasharray="3,3"/>\n`;
  g += `<text x="2" y="${(y5 + fs * 0.85).toFixed(1)}" font-size="${fs}" fill="#000" fill-opacity="0.4" font-family="sans-serif" font-weight="bold">5</text>\n`;
  g += `<line x1="3" y1="${los.toFixed(1)}" x2="${W - 3}" y2="${los.toFixed(1)}" stroke="#000" stroke-width="1.1"/>\n`;
  g += `<line x1="0" y1="${nameY.toFixed(1)}" x2="${W}" y2="${nameY.toFixed(1)}" stroke="#aaa" stroke-width="0.6"/>\n`;
  if (play) {
    g += `<text x="5" y="${(nameY + (H - nameY) / 2 + fs * 0.4).toFixed(1)}" font-size="8" font-weight="bold" font-family="sans-serif" fill="#111">${escapeHtml(play.name)}</text>\n`;
  }
  g += `<rect x="0.5" y="0.5" width="${W - 1}" height="${H - 1}" fill="none" stroke="#888" stroke-width="0.7"/>\n`;
  g += buildPlayElementsSVG(play, W, H, mode, refW);
  return { content: g, markerDefs };
}

export function buildStandaloneSVG(play: PlayData | null, mode: string): string {
  const pt = play?.playType || 'condensed';
  const dims = pbGetPlayDims(pt);
  const W = dims.w,
    H = dims.h;
  const { content, markerDefs } = buildPlaySVGContent(play, W, H, mode, W);
  const xmlDecl = '<' + '?xml version="1.0" encoding="UTF-8"?' + '>';
  return `${xmlDecl}\n<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">\n<defs>\n${markerDefs}\n</defs>\n${content}</svg>`;
}

/**
 * Standalone SVG for a drill diagram: the true-proportion field the scratch pad
 * draws, plus the play elements in 'scratch' mode so nothing is faded.
 *
 * A separate entry point on purpose — `buildStandaloneSVG` and
 * `buildPlaySVGContent` draw the 4.5"×2.25" play card and coaches' printed
 * cards depend on them byte for byte, so drill diagrams get their own field
 * rather than a flag threaded through those.
 */
export function buildDrillSVG(play: PlayData | null): string {
  const W = PB_DRILL_W,
    H = PB_DRILL_H;
  const pxYd = H / PB_SCR_YD_TOTAL;
  const los = H * PB_SCR_LOS_PCT;
  let g = `<rect width="${W}" height="${H}" fill="#f4fbf4"/>\n`;
  for (let yd = -PB_SCR_YD_DOWN; yd <= PB_SCR_YD_UP; yd++) {
    const y = los - yd * pxYd;
    const major = yd % 5 === 0;
    g += `<line x1="0" y1="${y.toFixed(1)}" x2="${W}" y2="${y.toFixed(1)}" stroke="#000" stroke-opacity="${major ? 0.35 : 0.08}" stroke-width="${major ? 1 : 0.6}"/>\n`;
    if (major && yd !== 0) {
      g += `<text x="3" y="${(y - 2).toFixed(1)}" font-size="9" font-weight="bold" font-family="sans-serif" fill="#000" fill-opacity="0.45">${Math.abs(yd)}</text>\n`;
    }
  }
  [0.5 - PB_SCR_HASH_OFF, 0.5 + PB_SCR_HASH_OFF].forEach((hx) => {
    for (let yd = -PB_SCR_YD_DOWN; yd <= PB_SCR_YD_UP; yd++) {
      const y = los - yd * pxYd;
      g += `<line x1="${(hx * W - 3).toFixed(1)}" y1="${y.toFixed(1)}" x2="${(hx * W + 3).toFixed(1)}" y2="${y.toFixed(1)}" stroke="#000" stroke-opacity="0.3" stroke-width="0.8"/>\n`;
    }
  });
  g += `<line x1="0" y1="${los.toFixed(1)}" x2="${W}" y2="${los.toFixed(1)}" stroke="#000" stroke-width="1.6"/>\n`;
  g += buildPlayElementsSVG(play, W, H, 'scratch', W * PB_SCR_ICON_REF, { stroke: 3, arrow: 12, cap: 8, zig: 9 });
  g += `<rect x="0.5" y="0.5" width="${W - 1}" height="${H - 1}" fill="none" stroke="#888" stroke-width="0.7"/>\n`;
  const xmlDecl = '<' + '?xml version="1.0" encoding="UTF-8"?' + '>';
  return `${xmlDecl}\n<svg width="${W}" height="${H}" viewBox="0 0 ${W} ${H}" xmlns="http://www.w3.org/2000/svg">\n${g}</svg>`;
}

export function svgFileName(play: PlayData | null): string {
  return (play?.name || 'play').replace(/\s+/g, '-') + '.svg';
}
